import { Box, Flex, Text } from "@mantine/core"
import { CheckIcon } from "@phosphor-icons/react";
import { StatusStepPopover, type HelpStatusBody } from "@dehub/theme"
import classes from "./status-bar.module.css"

type StatusStep = {
  code: string
  name: string
  color?: string
}

type Props = {
  statuses: StatusStep[]
  currentStatus: string
  /** Help content for a step (Юу вэ / Хэн / Дараа нь). null → the step is not interactive (R10, @dehub/help). */
  getStepHelp?: (code: string) => HelpStatusBody | null
  /** Fired when a step popover opens (telemetry). */
  onStepClick?: (status: StatusStep) => void
}

export function StatusBar({ statuses, currentStatus, getStepHelp, onStepClick }: Props) {
  const currentIndex = statuses.findIndex((s) => s.code === currentStatus)

  return (
    <Box className={classes.root}>
      <Flex align="center" className={classes.track}>
        {statuses.map((status, i) => {
          const isPast = i < currentIndex
          const isCurrent = i === currentIndex
          const isActive = isPast || isCurrent
          // Same scheme as OrderStepper: done green, current dark (the catalogue colour is not used here).
          const accent = isPast ? "var(--mantine-color-green-6)" : "var(--mantine-color-dark-8)"
          const state = isPast ? "past" : isCurrent ? "current" : "future"
          const help = getStepHelp?.(status.code) ?? null

          const step = (
            <Flex direction="column" align="center" gap={6} className={classes.step} aria-current={isCurrent ? "step" : undefined}>
              <Box
                className={classes.dot}
                data-state={state}
                style={
                  isPast
                    ? { background: accent, borderColor: accent }
                    : isCurrent
                    ? { borderColor: accent, boxShadow: `0 0 0 5px color-mix(in srgb, ${accent} 18%, transparent)` }
                    : undefined
                }
              >
                {isPast && <CheckIcon size={14} color="var(--mantine-color-white)" />}
                {isCurrent && (
                  <Box
                    className={classes.pulse}
                    style={{ background: accent }}
                  />
                )}
              </Box>
              <Text
                fz={14}
                fw={isCurrent ? 700 : isActive ? 600 : 500}
                c={isActive ? "var(--mantine-color-dark-7)" : "var(--mantine-color-gray-6)"}
                ta="center"
                style={{ whiteSpace: "nowrap", letterSpacing: isCurrent ? "0.01em" : 0 }}
              >
                {status.name}
              </Text>
            </Flex>
          )

          return (
            <Flex key={status.code} align="center" style={{ flex: i < statuses.length - 1 ? 1 : undefined }}>
              <StatusStepPopover help={help} title={status.name} onOpen={() => onStepClick?.(status)}>
                {step}
              </StatusStepPopover>
              {i < statuses.length - 1 && (
                <Box
                  className={classes.line}
                  data-active={isPast ? "true" : "false"}
                />
              )}
            </Flex>
          )
        })}
      </Flex>
    </Box>
  )
}
